import type { ModerationDetail, ModerationIntelligence } from '../types';
import type { ModerationActions } from '../hooks/useModerationActions';
import { isTerminalCaseStatus } from '../lib/verdictDisplay';

/** Reported user's account status code that means "currently banned" (parity with users' ban action). */
const USER_STATUS_TERMINATED = 'TER';

// Declarative action model (doc 09 §4.6). The confirm policy + toasts live in the action callbacks
// (useModerationActions); specs describe which buttons appear per variant and how they render.
export type ActionVariantKind = 'destructive' | 'reversible' | 'sideEffect' | 'positive';

export type ActionGroup =
  'Report Actions' | 'User Actions' | 'Feedback Actions' | 'Moderation Overrides';

export interface ActionSpec {
  id: string;
  label: string;
  group: ActionGroup;
  variant: ActionVariantKind;
  run: () => void | Promise<void>;
}

/**
 * Per-variant action sets (doc 09 §4.6). Take-Down/List (CONTENT), Remove-Content (CONTENT),
 * Remove-Comment (COMMENT) and Remove-Message (MESSAGE) live on their respective variant cards, not
 * this panel. Send Notification is real (POST /admin/api/moderation/send-notification) and shown for
 * every non-feedback kind, mirroring CMP's ActionsCard (ModerationDetailsActions.kt).
 */
export function buildActionSpecs(
  report: ModerationDetail,
  a: ModerationActions,
  intelligence?: ModerationIntelligence | null,
): ActionSpec[] {
  const isDismissed = report.status === 'DISMISSED';
  const reportAction: ActionSpec = isDismissed
    ? {
        id: 'reopen',
        label: 'Re-review',
        group: 'Report Actions',
        variant: 'reversible',
        run: a.reopen,
      }
    : {
        id: 'dismiss',
        label: 'Dismiss Report',
        group: 'Report Actions',
        variant: 'reversible',
        run: a.dismiss,
      };

  const notify: ActionSpec = {
    id: 'notify',
    label: 'Send Notification',
    group: 'Report Actions',
    variant: 'sideEffect',
    run: a.sendNotification,
  };

  // Coop intelligence override actions — only shown when intelligence is available with a case_id.
  const overrideSpecs: ActionSpec[] = [];
  if (intelligence?.case_id) {
    if (intelligence.verdict !== 'APPROVE') {
      overrideSpecs.push({
        id: 'forceApprove',
        label: 'Force Approve',
        group: 'Moderation Overrides',
        variant: 'positive',
        run: a.forceApprove,
      });
    }
    // TM-3567: keep Force Reject when Coop already says REJECT but TipMi post is still Waiting/Published.
    const verdictRejected = (intelligence.verdict ?? '').toUpperCase().includes('REJECT');
    const tipmiAlreadyHidden =
      report.kind === 'CONTENT' &&
      ['HID', 'HIDDEN', 'TER', 'TERMINATED'].includes((report.content.status ?? '').toUpperCase());
    if (!verdictRejected || (report.kind === 'CONTENT' && !tipmiAlreadyHidden)) {
      overrideSpecs.push({
        id: 'forceReject',
        label: 'Force Reject',
        group: 'Moderation Overrides',
        variant: 'destructive',
        run: a.forceReject,
      });
    }
    if (
      !isTerminalCaseStatus(intelligence.case_status) &&
      !(intelligence.signals ?? []).some((s) => s.channel === 'gpu')
    ) {
      overrideSpecs.push({
        id: 'escalateGpu',
        label: 'Run Frontier Model Pass',
        group: 'Moderation Overrides',
        variant: 'sideEffect',
        run: a.escalateGpu,
      });
    }
    if (intelligence.verdict !== 'ESCALATE (Human review)') {
      overrideSpecs.push({
        id: 'escalateHuman',
        label: 'Route to Human Review',
        group: 'Moderation Overrides',
        variant: 'sideEffect',
        run: a.escalateHuman,
      });
    }
  }

  switch (report.kind) {
    case 'USER': {
      // ONE toggle reflecting the reported user's CURRENT status (parity ModerationDetailsActions.kt:196-210):
      // shows "Ban User" while active, "Unban User" once terminated — never both at once.
      const isBanned = report.reportedUserStatus === USER_STATUS_TERMINATED;
      const banToggle: ActionSpec = isBanned
        ? {
            id: 'unban',
            label: 'Unban User',
            group: 'User Actions',
            variant: 'positive',
            run: a.unbanUser,
          }
        : {
            id: 'ban',
            label: 'Ban User',
            group: 'User Actions',
            variant: 'destructive',
            run: a.banUser,
          };
      return [reportAction, banToggle, notify, ...overrideSpecs];
    }
    case 'CONTENT':
    case 'COMMENT':
    case 'MESSAGE':
      return [reportAction, notify, ...overrideSpecs];
    case 'FEEDBACK':
      return [
        {
          id: 'sendEmail',
          label: 'Send Email to Feedbacker',
          group: 'Feedback Actions',
          variant: 'sideEffect',
          run: a.sendFeedbackEmail,
        },
        {
          id: 'solve',
          label: 'Solve Feedback',
          group: 'Feedback Actions',
          variant: 'positive',
          run: a.solveFeedback,
        },
        ...overrideSpecs,
      ];
    default:
      return [reportAction, ...overrideSpecs];
  }
}

/** Map the safety-model variant to a shadcn Button variant. */
export function actionButtonVariant(
  v: ActionVariantKind,
): 'default' | 'destructive' | 'secondary' | 'outline' {
  switch (v) {
    case 'destructive':
      return 'destructive';
    case 'reversible':
      return 'outline';
    case 'sideEffect':
      return 'secondary';
    case 'positive':
      return 'default';
    default:
      return 'outline';
  }
}
